// src/components/FilterDialog.jsx
import { useEffect, useState } from "react";
import "./FilterDialog.css";
import { getAllGenres } from "../../services/genreService"; // adjust path if needed

export default function FilterDialog({
                                         open,
                                         initialFilters = { year: "", ratingGte: "", genres: "" },
                                         onClose = () => {},
                                         onSave = () => {},
                                     }) {
    const [form, setForm] = useState(initialFilters);
    const [selected, setSelected] = useState(new Set());
    const [genreList, setGenreList] = useState([]); // [{id, name}]

    const clearAll = () => {
        setForm({ year: "", ratingGte: "", genres: "" });
        setSelected(new Set());
    };

    // (Re)hydrate form when opening
    useEffect(() => {
        if (open) setForm(initialFilters);
    }, [open, initialFilters]);

    // Load genres from the global map (no user input)
    useEffect(() => {
        if (!open) return;
        const map = getAllGenres(); // { [id]: name }
        const list = Object.entries(map).map(([id, name]) => ({ id: Number(id), name }));
        setGenreList(list);
    }, [open]);

    // Hydrate selected genres from CSV when opening
    useEffect(() => {
        if (!open) return;
        const csv = (initialFilters.genres || "").trim();
        setSelected(csv ? new Set(csv.split(",").map((s) => Number(s))) : new Set());
    }, [open, initialFilters.genres]);

    const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const toggleGenre = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const submit = (e) => {
        e.preventDefault();
        const genresCsv = Array.from(selected).join(","); // IDs CSV
        onSave({ ...form, genres: genresCsv });
        onClose();
    };

    if (!open) return null;

    return (
        <div className="modal" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
                <header className="modal-header">
                    <h3 style={{ margin: 0 }}>Filters</h3>
                    <button className="btn-ghost" onClick={onClose} aria-label="Close">
                        ✕
                    </button>
                </header>

                <form className="vstack form" onSubmit={submit}>
                    <label className="form-row">
                        <span>Release year</span>
                        <input
                            type="number"
                            className="input"
                            placeholder="e.g. 2024"
                            value={form.year}
                            onChange={update("year")}
                        />
                    </label>

                    <label className="form-row">
                        <span>Min rating</span>
                        <input
                            type="number"
                            min="0"
                            max="10"
                            step="0.1"
                            className="input"
                            placeholder="0–10"
                            value={form.ratingGte}
                            onChange={update("ratingGte")}
                        />
                    </label>

                    <div className="form-row">
                        <span>Genres</span>
                        <div className="tags">
                            {genreList.map((g) => {
                                const active = selected.has(g.id);
                                return (
                                    <button
                                        type="button"
                                        key={g.id}
                                        onClick={() => toggleGenre(g.id)}
                                        aria-pressed={active}
                                        className={`tag${active ? " is-active" : ""}`}
                                    >
                                        {g.name}
                                    </button>
                                );
                            })}
                            {genreList.length === 0 && <div className="meta">No genres loaded.</div>}
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="fd-btn ghost" onClick={clearAll}>
                            Clear filters
                        </button>
                        <button type="button" className="button fd-btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="button primary fd-btn">
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
